import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Button,
  TextField,
  Divider,
} from '@mui/material';
import { LockOutlined, Schedule, EmailOutlined, CheckCircleOutline } from '@mui/icons-material';
import { usePortalAuth } from '../context/PortalAuthContext';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

export const TokenLanding: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authenticate, isAuthenticated, error } = usePortalAuth();
  const [isProcessing, setIsProcessing] = useState(true);

  // Email login state
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    const redirect = searchParams.get('redirect');

    if (!token) {
      setIsProcessing(false);
      return;
    }

    authenticate(token).then((success) => {
      setIsProcessing(false);
      if (success) {
        navigate(redirect || '/', { replace: true });
      }
    });
  }, [searchParams, authenticate, navigate]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setEmailLoading(true);
    setEmailError('');

    try {
      // Extract subdomain from current hostname (skip "portal" which is the generic portal)
      const hostname = window.location.hostname;
      const sub = hostname.endsWith('.pooldial.com')
        ? hostname.replace('.pooldial.com', '')
        : undefined;
      const subdomain = sub && sub !== 'portal' ? sub : undefined;

      const response = await fetch(`${API_BASE_URL}/portal/request-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), subdomain }),
      });

      if (response.ok) {
        setEmailSent(true);
      } else {
        setEmailError(t('Something went wrong. Please try again.'));
      }
    } catch {
      setEmailError(t('Unable to connect. Please try again later.'));
    } finally {
      setEmailLoading(false);
    }
  };

  if (isProcessing) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <CircularProgress size={48} sx={{ mb: 3 }} />
        <Typography variant="h6" color="text.secondary">
          {t('Verifying your access...')}
        </Typography>
      </Box>
    );
  }

  if (isAuthenticated) {
    return null; // Will redirect
  }

  const token = searchParams.get('token');
  const isExpired = error?.toLowerCase().includes('expired') || error?.toLowerCase().includes('invalid');

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Paper
        sx={{
          maxWidth: 420,
          width: '100%',
          p: 4,
          textAlign: 'center',
        }}
      >
        {emailSent ? (
          <>
            <CheckCircleOutline sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('Check Your Email')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
              {t("If an account exists for {{email}}, we've sent a login link. Check your inbox and click the link to sign in.", { email })}
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={() => { setEmailSent(false); setEmail(''); }}
            >
              {t('Use a different email')}
            </Button>
          </>
        ) : !token ? (
          <>
            <LockOutlined sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('Customer Portal')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
              {t('Sign in to view your service history, invoices, and account information.')}
            </Typography>

            <Box component="form" onSubmit={handleEmailLogin}>
              <TextField
                fullWidth
                type="email"
                label={t('Email address')}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="small"
                required
                error={!!emailError}
                helperText={emailError}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={emailLoading || !email.trim()}
                startIcon={emailLoading ? <CircularProgress size={18} /> : <EmailOutlined />}
                sx={{ textTransform: 'none', py: 1.2 }}
              >
                {emailLoading ? t('Sending...') : t('Send me a login link')}
              </Button>
            </Box>

            <Divider sx={{ my: 3 }}>
              <Typography variant="caption" color="text.secondary">{t('or')}</Typography>
            </Divider>

            <Typography variant="body2" color="text.secondary">
              {t('Use the secure link from a recent email from your pool service provider.')}
            </Typography>
          </>
        ) : isExpired ? (
          <>
            <Schedule sx={{ fontSize: 56, color: 'warning.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('Link Expired')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
              {t('For your security, portal access links expire after a period of time.')}
            </Typography>

            <Box component="form" onSubmit={handleEmailLogin}>
              <TextField
                fullWidth
                type="email"
                label={t('Email address')}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="small"
                required
                error={!!emailError}
                helperText={emailError}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={emailLoading || !email.trim()}
                startIcon={emailLoading ? <CircularProgress size={18} /> : <EmailOutlined />}
                sx={{ textTransform: 'none', py: 1.2 }}
              >
                {emailLoading ? t('Sending...') : t('Send me a new login link')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <LockOutlined sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('Unable to Sign In')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
              {t("We couldn't verify your access link. This can happen if the link was copied incorrectly or has already been used.")}
            </Typography>

            <Box component="form" onSubmit={handleEmailLogin}>
              <TextField
                fullWidth
                type="email"
                label={t('Email address')}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="small"
                required
                error={!!emailError}
                helperText={emailError}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={emailLoading || !email.trim()}
                startIcon={emailLoading ? <CircularProgress size={18} /> : <EmailOutlined />}
                sx={{ textTransform: 'none', py: 1.2 }}
              >
                {emailLoading ? t('Sending...') : t('Send me a new login link')}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};
